// @ts-check
/* eslint-disable no-param-reassign */
import { forEach, isEmpty, isNil, map, reduce, isFinite } from "lodash";
import { roundFte } from "src/util/roundingStrategy";
import { DIRECT, INDIRECT_PLACEHOLDER } from "../../../macroAllocationTypes";
import { getLineItemsTotalCost } from "../setTotalCost";

import { getLineItemsWithType } from "../lineItemsWithType";

const calculateConstraintTotals = (constraints, columns) =>
  reduce(
    columns,
    (totals, { id: columnId, columnType }, /** @type {number} */ index) => {
      if (constraints?.[index]) {
        const { constraint, defaultedConstraint } = constraints[index];
        const isConstraintPresent = !isNil(constraint);
        const isPreviousConstraintTotalAllDefaults =
          totals[index - 1]?.isConstraintTotalAllDefaults ?? true;

        totals.push({
          columnId,
          columnType,
          constraintTotal: isConstraintPresent
            ? constraint
            : defaultedConstraint,
          isConstraintTotalAllDefaults:
            isPreviousConstraintTotalAllDefaults && !isConstraintPresent,
        });
        return totals;
      }

      totals.push({
        columnId,
        columnType,
        constraintTotal: 0,
        isConstraintTotalAllDefaults: true,
      });
      return totals;
    },
    []
  );

const zeroedLineItemTotals = (columns) =>
  map(columns, ({ id, columnType }) => ({
    columnId: id,
    columnType,
    total: 0,
    deltaTotal: 0,
    totalCost: 0,
  }));

const calculateLineItemTotals = (lineItems, columns) =>
  map(
    reduce(
      lineItems,
      (totals, lineItem) => {
        return map(totals, (total, index) => {
          const {
            value,
            defaultedValue,
            delta,
            totalCost: cellTotalCost,
          } = lineItem.cells[index];
          total.total += value ?? defaultedValue ?? 0;
          total.deltaTotal += delta ?? 0;
          total.totalCost += isFinite(cellTotalCost) ? cellTotalCost : 0;
          return total;
        });
      },
      zeroedLineItemTotals(columns)
    ),
    ({ columnId, columnType, total, deltaTotal, totalCost }) => ({
      columnId,
      columnType,
      total: roundFte(total),
      deltaTotal: roundFte(deltaTotal),
      totalCost,
    })
  );

const calculateLineItemTotalsByType = (lineItems, columns) => {
  const lineItemTotalsByType = {};
  const indirectPlaceholderLineItems = getLineItemsWithType(
    lineItems,
    INDIRECT_PLACEHOLDER
  );
  lineItemTotalsByType[INDIRECT_PLACEHOLDER] = calculateLineItemTotals(
    indirectPlaceholderLineItems,
    columns
  );
  lineItemTotalsByType[`totalCost-${INDIRECT_PLACEHOLDER}`] =
    getLineItemsTotalCost(indirectPlaceholderLineItems);

  const defaultLineItems = getLineItemsWithType(lineItems, null);
  lineItemTotalsByType[DIRECT] = calculateLineItemTotals(
    defaultLineItems,
    columns
  );
  lineItemTotalsByType[`totalCost-${DIRECT}`] =
    getLineItemsTotalCost(lineItems);

  return lineItemTotalsByType;
};

const zeroedGroupingsTotal = (columns) =>
  map(columns, ({ id, columnType }) => ({
    columnId: id,
    columnType,
    total: 0,
    deltaTotal: 0,
    totalCost: 0,
    constraintTotal: 0,
    isConstraintTotalAllDefaults: true,
  }));

const calculateChildGroupTotals = (childGroupings, columns) =>
  map(
    reduce(
      childGroupings,
      (totals, grouping) => {
        return map(totals, (total, index) => {
          const {
            total: subTotal,
            deltaTotal: deltaSubTotal,
            totalCost: subTotalCost,
            constraintTotal: constraintSubTotal,
            isConstraintTotalAllDefaults: isConstraintTotalAllDefaultsRHS,
          } = grouping.summedTotals[index];
          total.total += subTotal;
          total.deltaTotal += deltaSubTotal;
          total.totalCost += subTotalCost;
          total.constraintTotal += constraintSubTotal;
          total.isConstraintTotalAllDefaults &&=
            isConstraintTotalAllDefaultsRHS;
          return total;
        });
      },
      zeroedGroupingsTotal(columns)
    ),
    ({
      columnId,
      columnType,
      total,
      deltaTotal,
      totalCost,
      constraintTotal,
      isConstraintTotalAllDefaults,
    }) => ({
      columnId,
      columnType,
      total: roundFte(total),
      deltaTotal: roundFte(deltaTotal),
      totalCost,
      constraintTotal: roundFte(constraintTotal),
      isConstraintTotalAllDefaults,
    })
  );

const setGroupingWithCalculatedSumTotals = (
  grouping,
  columns,
  { recalcChildren = true, recalcParents = false } = {}
) => {
  if (recalcChildren && !isEmpty(grouping.childGroupings)) {
    forEach(grouping.childGroupings, (childGrouping) => {
      setGroupingWithCalculatedSumTotals(childGrouping, columns);
    });
  }

  const constraintTotals = calculateConstraintTotals(
    grouping.constraints,
    columns
  );
  const lineItemTotals = calculateLineItemTotals(grouping.lineItems, columns);
  const lineItemTotalsByType = calculateLineItemTotalsByType(
    grouping.lineItems,
    columns
  );
  const childGroupTotals = calculateChildGroupTotals(
    grouping.childGroupings,
    columns
  );

  grouping.lineItemTotals = lineItemTotals;
  grouping.lineItemTotalsByType = lineItemTotalsByType;
  grouping.summedTotals = map(columns, ({ id, columnType }, index) => {
    const lineItemTotalByColumn = lineItemTotals[index];
    const childGroupsTotalByColumn = childGroupTotals[index];
    const total = roundFte(
      lineItemTotalByColumn.total + childGroupsTotalByColumn.total
    );
    const deltaTotal = roundFte(
      lineItemTotalByColumn.deltaTotal + childGroupsTotalByColumn.deltaTotal
    );

    return {
      columnId: id,
      columnType,
      total,
      deltaTotal,
      totalCost:
        lineItemTotalByColumn.totalCost + childGroupsTotalByColumn.totalCost,
      constraintTotal: !isEmpty(grouping.childGroupings)
        ? childGroupsTotalByColumn.constraintTotal
        : constraintTotals[index].constraintTotal,
      isConstraintTotalAllDefaults: !isEmpty(grouping.childGroupings)
        ? childGroupsTotalByColumn.isConstraintTotalAllDefaults
        : constraintTotals[index].isConstraintTotalAllDefaults,
    };
  });

  if (recalcParents && grouping.parent) {
    setGroupingWithCalculatedSumTotals(grouping.parent, columns, {
      recalcChildren: false,
      recalcParents: true,
    });
  }
};

export default setGroupingWithCalculatedSumTotals;
